import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Container, Row, Col } from 'react-bootstrap';
import './About.css';

const About = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });



  const expertiseItems = [
    {
      icon: '/icons/expertise-calcul.svg',
      title: 'Conception et Calcul Structural',
      description: 'Analyse et dimensionnement de structures en béton, acier et bois selon les normes en vigueur.'
    },
    {
      icon: '/icons/expertise-rehabilitation.svg',
      title: 'Réhabilitation et Renforcement',
      description: 'Recalcul et vérification de structures existantes, conception de solutions de renforcement adaptées.'
    },
    {
      icon: '/icons/expertise-inspection.svg',
      title: 'Inspection et Diagnostic',
      description: 'Évaluation technique approfondie de l\'état des structures et recommandations d\'intervention.'
    },
    {
      icon: '/icons/expertise-bim.svg',
      title: 'Modélisation 3D et BIM',
      description: 'Utilisation de technologies de pointe pour la simulation, la détection des conflits et l\'optimisation des projets.'
    }
  ];

  const atoutsItems = [
    {
      icon: '/icons/atout-expertise.svg',
      title: 'Expertise & Expérience',
      description: 'Plus de 4 années d\'expertise reconnue dans le domaine de l\'ingénierie structurale.'
    },
    {
      icon: '/icons/atout-equipe.svg',
      title: 'Équipe Qualifiée',
      description: 'Une équipe passionnée composée d\'ingénieurs certifiés et de techniciens expérimentés.'
    },
    {
      icon: '/icons/atout-innovation.svg',
      title: 'Innovation Technologique',
      description: 'Maîtrise des outils de conception avancés (BIM) pour une qualité et une précision supérieures.'
    },
    {
      icon: '/icons/atout-client.svg',
      title: 'Approche Centrée Client',
      description: 'Engagement envers la satisfaction client, la transparence et la livraison de solutions économiquement viables.'
    }
  ];

  return (
    <section id="about" className="about-section" ref={ref}>
      <Container>

        {/* Main Content */}
        <Row className="align-items-start main-content">
          <Col lg={7}>
            <motion.div
              className="about-content"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="content-block">
                <h3 className="content-title">À propos de DHR Structures</h3>
                <p className="content-text">
                  DHR Structures est une entreprise d'ingénierie structurale de premier plan, spécialisée dans la conception,
                  l'analyse et la réhabilitation des structures. Notre expertise s'étend aux structures en béton, métallique et bois,
                  pour lesquelles nous réalisons des analyses et des calculs structuraux rigoureux basés sur les normes en vigueur.
                </p>
                <p className="content-text" style={{ marginTop: '1rem' }}>
                  Nous intégrons des moyens technologiques modernes à notre pratique, notamment la modélisation 3D et le BIM
                  (Building Information Modeling), pour garantir précision et efficacité. Que ce soit pour un nouveau projet ou
                  pour la réhabilitation et l'inspection de structures existantes, nous nous engageons à fournir des solutions
                  optimales, durables et sécuritaires, assurant la viabilité économique de chaque réalisation.
                </p>
              </div>

              <div className="content-block">
                <h3 className="content-title">Notre Mission</h3>
                <p className="content-text">
                  Notre mission est d'accompagner nos clients dans la concrétisation de leurs projets de construction et de
                  réhabilitation en leur offrant un service d'ingénierie structurale d'excellence. Nous nous distinguons par :
                </p>
                <ul className="mission-list">
                  <li>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>La fourniture d'analyses structurales rigoureuses et de conseils techniques avisés.</span>
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Une collaboration étroite avec nos clients, assurant un suivi personnalisé du début à la fin du projet.</span>
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>L'engagement à livrer des solutions innovantes, durables et conformes aux plus hauts standards de sécurité.</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </Col>

          <Col lg={5}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="image-container">
                <img
                  src="/images/utils/Image%20a%20propos.png"
                  alt="Équipe d'ingénieurs au travail"
                  className="about-image"
                />
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Notre Expertise */}
        <motion.div
          className="expertise-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="expertise-title">Notre Expertise</h3>
          <p className="expertise-intro">
            Notre savoir-faire technique est le pilier de notre succès. Nous offrons une gamme complète de services :
          </p>
          <Row className="g-4">
            {expertiseItems.map((item, index) => (
              <Col md={6} lg={3} key={index}>
                <motion.div
                  className="about-feature-card"
                  whileHover={{ translateY: -6 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="feature-icon">
                    <img src={item.icon} alt={item.title} style={{ width: '40px', height: '40px' }} />
                  </div>
                  <h4 className="feature-title">{item.title}</h4>
                  <p className="feature-desc">{item.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* Nos Atouts */}
        <motion.div
          className="atouts-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="expertise-title">Nos Atouts</h3>
          <Row className="g-4">
            {atoutsItems.map((item, index) => (
              <Col md={6} lg={3} key={index}>
                <motion.div
                  className="about-atout-card"
                  whileHover={{ translateY: -6 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="atout-icon">
                    <img src={item.icon} alt={item.title} style={{ width: '40px', height: '40px' }} />
                  </div>
                  <h4 className="atout-title">{item.title}</h4>
                  <p className="atout-desc">{item.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

      </Container>
    </section>
  );
};

export default About;